import { Types } from './header';
import { coalesce } from './ranges';

const INITIAL_STATE = {
  // [{ name, shortname, dates: { dayISO: cloud } }]
  missions: [],
  // flattened + sorted: [{ name, shortname, date, content }]
  availableDates: [],
  // [[startISO, endISO]] coalesced ranges already fetched
  cachedRanges: [],
  loading: false,
  // null | 'no-roi'
  error: null,
  // [{ missionName, date, id, thumbnail, clouds }] — consumed by ImageTable
  // the same way `state.acquisition.metadata` feeds the regular flow.
  metadata: [],
};

/**
 * @function flatten
 * @description Flatten per-mission date dictionaries into a sorted list of date entries.
 * `content` holds the cloud fraction (0..1), mirroring the shape the standard
 * PeriodChooser consumes (entry.date / entry.content).
 * @param {Array<{name, shortname, dates: Object<string, number>}>} missions
 * **Called by:
 * @see {mergeRangeResult}
 * @memberof module:ShoreS
 * @returns {Array<{name, shortname, date, content}>}
 */
const flatten = (missions) => {
  const out = [];
  missions.forEach((mission) => {
    Object.keys(mission.dates).forEach((date) => {
      out.push({
        name: mission.name,
        shortname: mission.shortname,
        date,
        content: mission.dates[date],
      });
    });
  });
  return out.sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * @function mergeRangeResult
 * @description Merge newly fetched missions into the existing cache WITHOUT discarding what
 * is already there (union of date dictionaries per mission).
 * @param {Object} state - Current Shore-S state.
 * @param {{missions: Array, ranges: Array<Array<string>>}} payload
 * **Called by:
 * @see {reducer}
 * @memberof module:ShoreS
 * @returns {Object} The next Shore-S state.
 */
const mergeRangeResult = (state, payload) => {
  const byName = {};
  state.missions.forEach((m) => {
    byName[m.name] = { name: m.name, shortname: m.shortname, dates: { ...m.dates } };
  });

  payload.missions.forEach((m) => {
    if (!byName[m.name]) {
      byName[m.name] = { name: m.name, shortname: m.shortname, dates: {} };
    }
    byName[m.name].dates = { ...byName[m.name].dates, ...m.dates };
  });

  const missions = Object.values(byName);

  return {
    ...state,
    missions,
    availableDates: flatten(missions),
    cachedRanges: coalesce([...state.cachedRanges, ...payload.ranges]),
    loading: false,
  };
};

export const reducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case Types.SET_LOADING:
      // Clearing the error when a new fetch starts keeps the UI clean.
      return { ...state, loading: action.payload.loading, error: action.payload.loading ? null : state.error };

    case Types.SET_ERROR:
      return { ...state, error: action.payload.error, loading: false };

    case Types.SET_RANGE_RESULT:
      return mergeRangeResult(state, action.payload);

    case Types.INSERT_METADATA:
      return {
        ...state,
        metadata: [
          ...state.metadata,
          {
            ...action.payload.metadata,
            missionName: action.payload.missionName,
            date: action.payload.date,
          },
        ],
      };

    case Types.RESET:
      return INITIAL_STATE;

    default:
      return state;
  }
};

export default reducer;
